import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Space, Avatar, Dropdown, Button, Select } from 'antd';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  ShopOutlined,
  TeamOutlined,
  LogoutOutlined,
  UserOutlined,
  ApiOutlined,
  DownOutlined,
  BookOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import { brand, colors } from '../theme';
import BrandLogomark from './common/BrandLogomark';
import RoleBadge from './common/RoleBadge';

const { Header } = Layout;

type NavSection = 'agents' | 'marketplace' | 'brain' | 'connectors' | 'playground' | 'none';

interface NavItem {
  id: Exclude<NavSection, 'none'>;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'agents',      label: 'Agentes',     path: '/',            icon: <ApiOutlined style={{ fontSize: 12 }} /> },
  { id: 'marketplace', label: 'Marketplace', path: '/marketplace', icon: <ShopOutlined style={{ fontSize: 12 }} /> },
  { id: 'brain',       label: 'Brain',       path: '/brain',       icon: <BookOutlined style={{ fontSize: 12 }} /> },
  { id: 'connectors',  label: 'Conectores',  path: '/connectors',  icon: <LinkOutlined style={{ fontSize: 12 }} /> },
  { id: 'playground',  label: 'Playground',  path: '/playground',  icon: <ThunderboltOutlined style={{ fontSize: 12 }} /> },
];

function initials(email: string | null): string {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split('.');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AppHeader: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { email, role, canEdit, isAdmin, logout, isSuperadmin, tenants, activeTenant, selectTenant } = useUser();

  const activeSection = (): NavSection => {
    const p = location.pathname;
    if (p.startsWith('/marketplace')) return 'marketplace';
    if (p.startsWith('/brain')) return 'brain';
    if (p.startsWith('/connectors')) return 'connectors';
    if (p.startsWith('/playground')) return 'playground';
    if (p === '/' || p.startsWith('/builder')) return 'agents';
    return 'none';
  };
  const active = activeSection();

  const profileMenu: MenuProps = {
    items: [
      {
        key: 'info',
        label: (
          <div style={{ padding: '4px 0', minWidth: 180 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{email}</div>
            <div style={{ marginTop: 4 }}>
              <RoleBadge role={role} variant="chip" />
            </div>
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Settings',
        onClick: () => navigate('/settings'),
      },
      ...(isAdmin ? [{
        key: 'organization',
        icon: <BankOutlined />,
        label: 'Administrar Organización',
        onClick: () => navigate('/organization'),
      }] : []),
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Cerrar sesión',
        danger: true,
        onClick: logout,
      },
    ],
  };

  const renderNavBtn = (item: NavItem) => {
    const on = active === item.id;
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '5px 11px', borderRadius: 6,
          fontSize: 13, fontWeight: on ? 600 : 400,
          cursor: 'pointer', border: 'none',
          color: on ? colors.primary : '#4b5563',
          background: on ? colors.primarySoft : 'transparent',
          transition: 'all 0.15s',
        }}
      >
        {item.icon} {item.label}
      </button>
    );
  };

  return (
    <Header style={{
      background: '#fff',
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      height: 56,
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      {/* ── Left: brand + nav ── */}
      <Space align="center" size={0}>
        {/* Logo */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 20, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <BrandLogomark size={28} />
          <span style={{ color: '#141414', fontWeight: 700, fontSize: 14 }}>{brand.name}</span>
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 18, background: '#f0f0f0', marginRight: 8 }} />

        {/* Nav */}
        <Space size={2}>
          {NAV_ITEMS.map(renderNavBtn)}
        </Space>
      </Space>

      {/* ── Right: tenant selector (superadmin) + new agent + profile ── */}
      <Space align="center" size={12}>
        {isSuperadmin && tenants.length > 0 && (
          <Select
            size="small"
            value={activeTenant ?? ''}
            onChange={(v) => selectTenant(v || null)}
            style={{ minWidth: 160 }}
            title="Cliente (tenant) activo"
            options={[
              { value: '', label: 'Todos los clientes' },
              ...tenants.map((t) => ({ value: t.slug, label: t.name })),
            ]}
          />
        )}

        {canEdit && (
          <Button
            icon={<PlusOutlined />}
            onClick={() => navigate('/builder/new')}
            size="small"
            style={{ borderRadius: 6, fontWeight: 500, fontSize: 13 }}
          >
            Nuevo agente
          </Button>
        )}

        <Dropdown menu={profileMenu} trigger={['click']} placement="bottomRight">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 0' }}>
            <Avatar
              size={28}
              style={{
                background: colors.primary,
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
              icon={!email ? <UserOutlined /> : undefined}
            >
              {email ? initials(email) : undefined}
            </Avatar>
            <span style={{ fontSize: 12, color: '#595959', fontWeight: 500, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {email?.split('@')[0]}
            </span>
            <DownOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />
          </div>
        </Dropdown>
      </Space>
    </Header>
  );
};

export default AppHeader;
